import {
  AimOutlined,
  AppstoreOutlined,
  ArrowLeftOutlined,
  BulbOutlined,
  CheckCircleFilled,
  CompassOutlined,
  CopyOutlined,
  DollarOutlined,
  ExclamationCircleFilled,
  EyeOutlined,
  GlobalOutlined,
  InfoCircleOutlined,
  RobotOutlined,
  RocketOutlined,
  SmileOutlined,
  SoundOutlined,
  SoundTwoTone,
  TeamOutlined,
  WarningFilled,
} from '@ant-design/icons'
import { Alert, Button, Modal, Skeleton, Tag, Tooltip, message } from 'antd'
import { useCallback, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import {
  confirmVirtualIpPositioningProfile,
  getVirtualIpDraft,
  getVirtualIpPositioningProfile,
  getVirtualIpProposals,
  getVirtualIpTypeRecommendation,
  reopenVirtualIpPositioningProfile,
  type SaasApiError,
  type VirtualIpDraft,
  type VirtualIpPositioningProfile,
  type VirtualIpProposals,
  type VirtualIpTypeRecommendation,
} from '../../api'
import VirtualIpStepper from './VirtualIpStepper'
import { virtualIpCompletedThrough } from './virtualIpProgress'
import { useResolvedVirtualIpProgress } from './useResolvedVirtualIpProgress'
import { useVirtualIpWorkflowContext } from './VirtualIpWorkflowContext'
import { confirmUpstreamEditIfNeeded } from './virtualIpUpstreamEdit'
import { VIRTUAL_IP_FLOW_TOTAL_STEPS } from './virtualIpFlow'
import './virtualIp.css'

/** VISTOERA_VIRTUAL_IP_STEP4_FINAL_CONFIRMATION_UI_V2 — visual/IA upgrade
 * only. Every field rendered below is read from the already-existing
 * canonical endpoints:
 *  - `GET .../positioning-profile` → `profile_preview` / `confirmed_profile`
 *    (built by the backend's pure `build_positioning_profile_v1`, itself a
 *    deterministic projection of the already-persisted STEP 1 intake +
 *    STEP 2 `selected_ip_type` + STEP 3 `selected_proposal_json` — this
 *    page never re-derives or invents any of that content).
 *  - `GET .../type-recommendation` → STEP 2 lineage (`selected.snapshot`:
 *    `display_name` + `suitability_score`, `selected.source_run_id`).
 *  - `GET .../proposals` → STEP 3 lineage (`selected_proposal`:
 *    `match_score`, `growth_potential`), used ONLY for the confirmation
 *    sidebar / visual-summary metadata that `build_positioning_profile_v1`
 *    does not copy into the profile itself — same underlying
 *    `selected_proposal_json`, so there is no risk of divergence.
 * No new backend route, schema, or AI call was added for this round. */

type PageState = 'loading' | 'error' | 'needs_step3' | 'ready'

const GROWTH_POTENTIAL_KEYS: Record<string, string> = { high: 'growthPotential.high', medium: 'growthPotential.medium', low: 'growthPotential.low' }

function knownPlatformLabels(t: (key: string) => string): Record<string, string> {
  return {
    douyin: t('platforms.douyin'),
    抖音: t('platforms.douyin'),
    xiaohongshu: t('platforms.xiaohongshu'),
    小红书: t('platforms.xiaohongshu'),
    wechat_channels: t('platforms.wechat_channels'),
    视频号: t('platforms.wechat_channels'),
    微信视频号: t('platforms.wechat_channels'),
    bilibili: t('platforms.bilibili'),
    b站: t('platforms.bilibili'),
    kuaishou: t('platforms.kuaishou'),
    快手: t('platforms.kuaishou'),
    youtube: 'YouTube',
    tiktok: 'TikTok',
    instagram: 'Instagram',
    微博: t('platforms.weibo'),
    weibo: t('platforms.weibo'),
  }
}

function knownPlatformLabel(t: (key: string) => string, raw: string): string | null {
  const map = knownPlatformLabels(t)
  const key = raw.trim().toLowerCase()
  return map[key] ?? map[raw.trim()] ?? null
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string' && v.length > 0) : []
}

function asText(value: unknown): string {
  return typeof value === 'string' && value.trim() ? value.trim() : ''
}

function copyToClipboard(text: string, t: (key: string) => string) {
  navigator.clipboard?.writeText(text).then(
    () => message.success(t('common.copySuccess')),
    () => message.error(t('common.copyFailed')),
  )
}

export default function VirtualIpStep4Page() {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const { workspaceId = '', projectId = '' } = useParams()
  const navigate = useNavigate()

  const [draft, setDraft] = useState<VirtualIpDraft | null>(null)
  const { refreshProgress } = useVirtualIpWorkflowContext()
  const progress = useResolvedVirtualIpProgress()
  const [profile, setProfile] = useState<VirtualIpPositioningProfile | null>(null)
  const [typeRec, setTypeRec] = useState<VirtualIpTypeRecommendation | null>(null)
  const [proposals, setProposals] = useState<VirtualIpProposals | null>(null)
  const [pageState, setPageState] = useState<PageState>('loading')
  const [loadError, setLoadError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [reopening, setReopening] = useState(false)

  const loadProfile = useCallback(
    (draftId: string) => {
      return getVirtualIpPositioningProfile(workspaceId, draftId).then((r) => {
        setProfile(r.data)
        return r.data
      })
    },
    [workspaceId],
  )

  useEffect(() => {
    if (!workspaceId) return
    let cancelled = false
    setPageState('loading')
    setLoadError(null)
    getVirtualIpDraft(workspaceId, projectId)
      .then((dRes) => {
        if (cancelled) return
        setDraft(dRes.data)
        if (!dRes.data.selected_proposal_id || virtualIpCompletedThrough(dRes.data.status) < 3) {
          setPageState('needs_step3')
          return
        }
        // STEP 2 / STEP 3 lineage cards are best-effort: their fetch
        // failing must never block the canonical profile from loading.
        getVirtualIpTypeRecommendation(workspaceId, dRes.data.id)
          .then((r) => !cancelled && setTypeRec(r.data))
          .catch(() => undefined)
        getVirtualIpProposals(workspaceId, dRes.data.id)
          .then((r) => !cancelled && setProposals(r.data))
          .catch(() => undefined)
        return loadProfile(dRes.data.id).then(() => {
          if (cancelled) return
          setPageState('ready')
        })
      })
      .catch((e) => {
        if (cancelled) return
        setLoadError((e as SaasApiError).message || t('common.loadFailedRefresh'))
        setPageState('error')
      })
    return () => {
      cancelled = true
    }
  }, [workspaceId, loadProfile])

  const handleConfirm = () => {
    if (!draft || !profile?.profile_preview) return
    const preview = profile.profile_preview
    Modal.confirm({
      title: t('step4.title'),
      content: t('step4.confirmContent', { name: String(preview.name ?? t('step4.defaultProposalName')) }),
      okText: t('flow.steps.4.label'),
      cancelText: t('actions:cancel'),
      centered: true,
      onOk: () => {
        setConfirming(true)
        return confirmVirtualIpPositioningProfile(workspaceId, draft.id)
          .then((r) => {
            setProfile(r.data)
            setConfirming(false)
            message.success(t('step4.confirmSuccess'))
            return getVirtualIpDraft(workspaceId, projectId).then((d) => {
              setDraft(d.data)
              return refreshProgress(d.data)
            })
          })
          .catch((e) => {
            setConfirming(false)
            message.error((e as SaasApiError).message || t('common.confirmFailedRetry'))
          })
      },
    })
  }

  const handleReopen = () => {
    if (!draft) return
    const runReopen = () => {
      setReopening(true)
      return reopenVirtualIpPositioningProfile(workspaceId, draft.id)
        .then((r) => {
          setDraft(r.data)
          setReopening(false)
          return refreshProgress(r.data).then(() => loadProfile(r.data.id))
        })
        .catch((e) => {
          setReopening(false)
          message.error((e as SaasApiError).message || t('common.operationFailed'))
        })
    }
    // Reopen undoes STEP4 confirmation — warn when profile was confirmed
    // (downstream STEP5+ artifacts may already exist; do not delete assets).
    confirmUpstreamEditIfNeeded({
      fromStep: 4,
      hasDownstreamArtifacts: progress.furthest_reached_step >= 5,
      titleKey: 'upstreamEdit.reopenTitle',
      onConfirm: async () => {
        await runReopen()
      },
    })
  }

  const goToStep3 = () => navigate(`/workspace/${workspaceId}/virtual-ip/projects/${projectId}/step-3`)
  const goToStep5 = () => navigate(`/workspace/${workspaceId}/virtual-ip/projects/${projectId}/step-5`)

  if (pageState === 'loading') {
    return (
      <div className="tc-vip-page tc-vip-step4-page">
        <Skeleton active paragraph={{ rows: 2 }} />
        <Skeleton active paragraph={{ rows: 8 }} style={{ marginTop: 20 }} />
      </div>
    )
  }

  if (pageState === 'error') {
    return (
      <div className="tc-vip-page tc-vip-step4-page">
        <Alert type="error" showIcon message={t('step4.confirmFailed')} description={loadError} />
      </div>
    )
  }

  if (pageState === 'needs_step3') {
    return (
      <div className="tc-vip-page tc-vip-step4-page" data-testid="virtual-ip-step4-needs-step3">
        <TopBar
          onBack={goToStep3}
          backLabel={t('common.backToPrevious')}
          confirmDisabled
          confirmDisabledReason={t('step4.confirmDisabledNoStep3')}
          onConfirm={() => undefined}
          confirming={false}
          onGoToStep5={goToStep5}
        />
        <VirtualIpStepper currentStep={4} progress={progress} />
        <Alert
          type="info"
          showIcon
          message={t('step4.prerequisiteMessage')} description={t('step4.prerequisiteDesc')} action={
            <Button type="primary" size="small" onClick={goToStep3}>
              {t('common.backToStep', { step: 3 })}
            </Button>
          }
        />
      </div>
    )
  }

  const status = profile?.status ?? 'LOADING'
  const isConfirmed = status === 'CONFIRMED'
  const displayProfile = isConfirmed ? profile?.confirmed_profile ?? null : profile?.profile_preview ?? null
  const staleDisplayProfile = status === 'STALE' ? profile?.confirmed_profile ?? null : null

  return (
    <div className="tc-vip-page tc-vip-step4-page" data-testid="virtual-ip-step4-page">
      <TopBar
        onBack={goToStep3}
        backLabel={t('common.backToPrevious')}
        isConfirmed={isConfirmed}
        confirmDisabled={status !== 'READY_TO_CONFIRM'}
        confirmDisabledReason={
          status === 'STALE'
            ? t('step4.upstreamChanged')
            : status === 'ERROR'
              ? t('step4.missingPrerequisite')
              : ''
        }
        confirming={confirming}
        onConfirm={handleConfirm}
        onReopen={handleReopen}
        reopening={reopening}
        onGoToStep5={goToStep5}
      />

      <VirtualIpStepper currentStep={4} progress={progress} />

      {status === 'STALE' && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningFilled />}
          message={t('step4.staleMessage')} description={profile?.stale_reason || t('step4.staleDefaultDesc')}
          action={
            <Button size="small" onClick={goToStep3}>
              {t('common.backToStep', { step: 3 })}
            </Button>
          }
          data-testid="vip-profile-stale-banner"
          style={{ marginBottom: 16 }}
        />
      )}

      {status === 'ERROR' && (
        <Alert
          type="error"
          showIcon
          icon={<ExclamationCircleFilled />}
          message={t('step4.confirmFailed')} description={t('step4.missingPrerequisite')}
          data-testid="vip-profile-error"
        />
      )}

      {(displayProfile || staleDisplayProfile) && (
        <>
          <FinalConfirmationHero isConfirmed={isConfirmed} />

          <div className="tc-vip-step4-grid" data-testid="vip-profile-preview">
            <ProfileCard profile={(displayProfile || staleDisplayProfile) as Record<string, unknown>} isConfirmed={isConfirmed} />
            <VisualSummaryColumn profile={(displayProfile || staleDisplayProfile) as Record<string, unknown>} growthPotential={proposalGrowthPotential(proposals)} />
            <ConfirmationSidebar
              profile={(displayProfile || staleDisplayProfile) as Record<string, unknown>}
              typeRec={typeRec}
              proposals={proposals}
              isConfirmed={isConfirmed}
              onGoToStep5={goToStep5}
            />
          </div>

          <div className="tc-vip-step4-footer-tip">
            <InfoCircleOutlined />
            <span>{t('step4.confirmBanner')}</span>
          </div>
        </>
      )}

      {status === 'READY_TO_CONFIRM' && !displayProfile && (
        <div className="tc-data-card tc-vip-card">
          <div className="tc-vip-card-title">{t('common.pendingGenerate')}</div>
          <p className="tc-vip-card-desc">{t('step4.notReadyDesc')}</p>
        </div>
      )}
    </div>
  )
}

function proposalGrowthPotential(proposals: VirtualIpProposals | null): string {
  const raw = proposals?.selected_proposal as Record<string, unknown> | null
  return typeof raw?.growth_potential === 'string' ? raw.growth_potential : ''
}

function proposalMatchScore(proposals: VirtualIpProposals | null): number | null {
  const raw = proposals?.selected_proposal as Record<string, unknown> | null
  return typeof raw?.match_score === 'number' ? raw.match_score : null
}

/** Top action bar (spec §5): back-left, all confirmation actions
 * top-right. "{t('common.saveAsDraft')}" is intentionally rendered disabled — every
 * STEP 2/3 selection is already persisted the moment the user makes it
 * (see `virtual_ip_type_recommendation_service.select_ip_type` /
 * `virtual_ip_proposals_service.select_proposal`), and STEP 4 itself has
 * no separate editable draft state to save; there is no real "save
 * draft" contract for this screen to wire up (final report:
 * `SAVE_DRAFT_CONTRACT = MISSING`), so this never fakes a save action. */
function TopBar({
  onBack,
  backLabel,
  isConfirmed,
  confirmDisabled,
  confirmDisabledReason,
  confirming,
  onConfirm,
  onReopen,
  reopening,
  onGoToStep5,
}: {
  onBack: () => void
  backLabel: string
  isConfirmed?: boolean
  confirmDisabled: boolean
  confirmDisabledReason: string
  confirming: boolean
  onConfirm: () => void
  onReopen?: () => void
  reopening?: boolean
  onGoToStep5: () => void
}) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const navigate = useNavigate()
  const { workspaceId = '' } = useParams()
  return (
    <div className="tc-vip-step4-topbar">
      <div>
        <Button
          className="tc-vip-step4-topbar-back"
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={onBack}
          data-testid="vip-step4-prev-button"
        >
          {backLabel}
        </Button>
        <div className="tc-page-header tc-vip-step4-topbar-titles" style={{ marginBottom: 0 }}>
          <div>
            <h1 className="tc-page-title">{t('common.pageTitle')}</h1>
            <div className="tc-vip-step-label">
              <span>STEP 4 / {VIRTUAL_IP_FLOW_TOTAL_STEPS}</span>
              <span className="tc-vip-step-title">{t('step4.title')}</span>
            </div>
            <p className="tc-page-desc">{t('step4.description')}</p>
          </div>
        </div>
      </div>
      <div className="tc-vip-step4-topbar-actions">
        {isConfirmed ? (
          <>
            <Button loading={reopening} onClick={onReopen} data-testid="vip-step4-reopen-button">
              {t('step4.reopenEdit')}
            </Button>
            <Button type="primary" onClick={onGoToStep5} data-testid="vip-step4-next-phase-button">
              {t('step4.nextCharacter')}
            </Button>
          </>
        ) : (
          <>
            <Tooltip title={t('step4.saveDraftTooltip')}>
              <span>
                <Button disabled data-testid="vip-step4-save-draft-button">
                  {t('common.saveAsDraft')}
                </Button>
              </span>
            </Tooltip>
            {confirmDisabled ? (
              <Tooltip title={confirmDisabledReason}>
                <span>
                  <Button type="primary" disabled data-testid="vip-step4-confirm-button">
                    {t('step4.confirmAndGenerate')}
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Button type="primary" loading={confirming} onClick={onConfirm} data-testid="vip-step4-confirm-button">
                {t('step4.confirmAndGenerate')}
              </Button>
            )}
          </>
        )}
        <Button size="small" type="text" onClick={() => navigate(`/workspace/${workspaceId}/overview`)}>
          {t('common.backToOverview')}
        </Button>
      </div>
    </div>
  )
}

/** Generic abstract "profile / dossier" illustration — decorative only,
 * never business data, never IP-type-specific (spec §6: "不要 hardcode
 * 茶壶。如果没有可靠动态 illustration：使用通用 Virtual IP / profile
 * abstract graphic"). */
function ProfileAbstractIllustration() {
  return (
    <svg width="72" height="72" viewBox="0 0 72 72" fill="none" aria-hidden="true" className="tc-vip-step4-hero-illustration">
      <circle cx="36" cy="26" r="12" stroke="currentColor" strokeWidth="2.5" opacity="0.9" />
      <path
        d="M14 60c0-13 10-22 22-22s22 9 22 22"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        opacity="0.9"
      />
      <path d="M8 66h56" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.35" />
      <circle cx="60" cy="14" r="3" fill="currentColor" opacity="0.6" />
      <circle cx="10" cy="20" r="2" fill="currentColor" opacity="0.4" />
    </svg>
  )
}

function FinalConfirmationHero({ isConfirmed }: { isConfirmed: boolean }) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  return (
    <div className="tc-vip-step4-hero-banner" data-testid="vip-step4-hero">
      <div className="tc-vip-step4-hero-main">
        <div className={`tc-vip-step4-hero-check${isConfirmed ? '' : ' is-pending'}`}>
          <CheckCircleFilled />
        </div>
        <div>
          <h2 className="tc-vip-step4-hero-title">
            {isConfirmed ? t('step4.heroConfirmedTitle') : t('step4.heroPendingTitle')}
          </h2>
          <p className="tc-vip-step4-hero-desc">
            {isConfirmed
              ? t('step4.heroConfirmedDesc')
              : t('step4.heroPendingDesc')}
          </p>
        </div>
      </div>
      <ProfileAbstractIllustration />
    </div>
  )
}

const FIELD_ACCENTS: Record<string, { color: string; bg: string }> = {
  purple: { color: 'var(--vip-c-purple)', bg: 'var(--vip-c-purple-bg)' },
  indigo: { color: 'var(--vip-c-indigo)', bg: 'var(--vip-c-indigo-bg)' },
  blue: { color: 'var(--vip-c-blue)', bg: 'var(--vip-c-blue-bg)' },
  cyan: { color: 'var(--vip-c-cyan)', bg: 'var(--vip-c-cyan-bg)' },
  green: { color: 'var(--vip-c-green)', bg: 'var(--vip-c-green-bg)' },
  orange: { color: 'var(--vip-c-orange)', bg: 'var(--vip-c-orange-bg)' },
  coral: { color: 'var(--vip-c-coral)', bg: 'var(--vip-c-coral-bg)' },
}

function FieldRow({
  accent,
  icon,
  label,
  children,
}: {
  accent: keyof typeof FIELD_ACCENTS
  icon: ReactNode
  label: string
  children: ReactNode
}) {
  const a = FIELD_ACCENTS[accent]
  return (
    <div className="tc-vip-step4-row">
      <span
        className="tc-vip-field-icon"
        style={{ '--field-color': a.color, '--field-bg': a.bg } as CSSProperties}
        aria-hidden
      >
        {icon}
      </span>
      <div className="tc-vip-step4-row-body">
        <span className="tc-vip-step4-row-label">{label}</span>
        <div className="tc-vip-step4-row-content">{children}</div>
      </div>
    </div>
  )
}

function ChipList({ items, className }: { items: string[]; className?: string }) {
  if (items.length === 0) return null
  return (
    <div className={`tc-vip-step4-chip-row ${className ?? ''}`.trim()}>
      {items.map((item, idx) => (
        <Tag key={idx} className="tc-vip-analysis-tag">
          {item}
        </Tag>
      ))}
    </div>
  )
}

function PlatformChipList({ items }: { items: string[] }) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  if (items.length === 0) return null
  return (
    <div className="tc-vip-step4-chip-row">
      {items.map((item, idx) => {
        const known = knownPlatformLabel(t, item)
        return (
          <Tag key={idx} className="tc-vip-analysis-tag" icon={known ? <GlobalOutlined /> : undefined}>
            {known ?? item}
          </Tag>
        )
      })}
    </div>
  )
}

/** Left column — Final IP Profile "dossier" card (spec §7/§8/§10/§11). */
function ProfileCard({ profile, isConfirmed }: { profile: Record<string, unknown>; isConfirmed: boolean }) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const name = asText(profile.name) || t('common.pendingGenerate')
  const positioningLabel = asText(profile.positioning_label)
  const oneLiner = asText(profile.one_liner)
  const ipTypeDisplay = asText(profile.selected_ip_type_display_name) || asText(profile.selected_ip_type)
  const targetAudience = asText(profile.target_audience)
  const coreValueProposition = asText(profile.core_value_proposition)
  const commercialDirection = asStringArray(profile.commercial_direction)
  const personaTraits = asStringArray(profile.persona_traits)
  const contentDirection = asStringArray(profile.content_direction)
  const primaryPlatforms = asStringArray(profile.primary_platforms)
  const visualStyle = asText(profile.visual_style)
  const voiceStyle = asText(profile.voice_style)
  const differentiation = asText(profile.differentiation)
  const tags = asStringArray(profile.tags)
  const revision = typeof profile.positioning_revision === 'number' ? profile.positioning_revision : null

  return (
    <div className="tc-data-card tc-vip-step4-profile-card" data-testid="vip-step4-profile-card">
      <div className="tc-vip-step4-hero">
        <div className="tc-vip-step4-avatar-placeholder" aria-hidden="true" data-testid="vip-step4-avatar-placeholder">
          <SmileOutlined />
        </div>
        <div className="tc-vip-step4-identity">
          <div className="tc-vip-step4-name-row">
            <h2 className="tc-vip-step4-name">{name}</h2>
            <span className={`tc-vip-step4-final-badge${isConfirmed ? ' is-confirmed' : ''}`}>
              {isConfirmed ? t('common.confirmed') : t('fields.finalPositioning')}
            </span>
          </div>
          {positioningLabel && <div className="tc-vip-step4-label">{positioningLabel}</div>}
          {oneLiner && <p className="tc-vip-step4-oneliner">「{oneLiner}」</p>}
        </div>
      </div>
      <p className="tc-vip-step4-visual-caption" data-testid="vip-step4-visual-source">
        {t('step4.visualPlaceholderCaption')}
      </p>

      <div className="tc-vip-step4-quick-grid">
        {ipTypeDisplay && (
          <QuickBlock label={t('flow.steps.2.label')} value={ipTypeDisplay} />
        )}
        {name && <QuickBlock label={t('flow.steps.3.label')} value={`${name}${positioningLabel ? `「${positioningLabel}」` : ''}`} />}
        {personaTraits.length > 0 && <QuickBlock label={t('fields.personaTraits')} value={personaTraits.join(' · ')} />}
        {revision !== null && <QuickBlock label={t('fields.positioningVersion')} value={t('common.revisionVersion', { revision })} />}
      </div>

      <div className="tc-vip-step4-rows">
        <FieldRow accent="purple" icon={<AppstoreOutlined />} label={t('flow.steps.2.label')}>
          {ipTypeDisplay || t('common.noData')}
        </FieldRow>
        {coreValueProposition && (
          <FieldRow accent="indigo" icon={<AimOutlined />} label={t('fields.corePositioning')}>
            {coreValueProposition}
          </FieldRow>
        )}
        {targetAudience && (
          <FieldRow accent="blue" icon={<TeamOutlined />} label={t('fields.targetAudience')}>
            {targetAudience}
          </FieldRow>
        )}
        {commercialDirection.length > 0 && (
          <FieldRow accent="green" icon={<DollarOutlined />} label={t('fields.commercialDirection')}>
            <ChipList items={commercialDirection} />
          </FieldRow>
        )}
        {personaTraits.length > 0 && (
          <FieldRow accent="cyan" icon={<SmileOutlined />} label={t('fields.personaTraits')}>
            <ChipList items={personaTraits} />
          </FieldRow>
        )}
        {contentDirection.length > 0 && (
          <FieldRow accent="coral" icon={<CompassOutlined />} label={t('fields.contentDirection')}>
            <ChipList items={contentDirection} />
          </FieldRow>
        )}
        {primaryPlatforms.length > 0 && (
          <FieldRow accent="orange" icon={<GlobalOutlined />} label={t('fields.primaryPlatforms')}>
            <PlatformChipList items={primaryPlatforms} />
          </FieldRow>
        )}
        {visualStyle && (
          <FieldRow accent="purple" icon={<EyeOutlined />} label={t('fields.visualStyle')}>
            {visualStyle}
          </FieldRow>
        )}
        {voiceStyle && (
          <FieldRow accent="indigo" icon={<SoundOutlined />} label={t('fields.voiceStyle')}>
            {voiceStyle}
          </FieldRow>
        )}
      </div>

      {differentiation && (
        <div className="tc-vip-step4-diff-box" data-testid="vip-step4-differentiation">
          <div className="tc-vip-step4-diff-head">
            <BulbOutlined />
            <span>{t('step4.differentiatedAdvantage')}</span>
          </div>
          <p>{differentiation}</p>
        </div>
      )}

      {tags.length > 0 && (
        <div className="tc-vip-step4-tags-block">
          <div className="tc-vip-subfield-title">{t('step3.sectionTags')}</div>
          <ChipList items={tags} />
        </div>
      )}
    </div>
  )
}

function QuickBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="tc-vip-step4-quick-block">
      <span className="tc-vip-step4-quick-label">{label}</span>
      <div className="tc-vip-step4-quick-value">{value}</div>
    </div>
  )
}

/** Middle column — t('step4.overviewTitle') + {t('step4.traitRadarTitle')} + {t('step4.coreKeywords')} + 说话风格
 * 示例 (spec §17–§22). `growthPotential` is the ONLY value here sourced
 * from the separate `/proposals` lineage fetch rather than the profile
 * itself (the profile schema never copies `growth_potential` — see
 * `build_positioning_profile_v1`); it is labeled with its real field
 * semantics (t('fields.growthPotential')), never invented as a fictitious "长期发展方向". */
function VisualSummaryColumn({ profile, growthPotential }: { profile: Record<string, unknown>; growthPotential: string }) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const coreValueProposition = asText(profile.core_value_proposition)
  const contentDirection = asStringArray(profile.content_direction)
  const commercialDirection = asStringArray(profile.commercial_direction)
  const primaryPlatforms = asStringArray(profile.primary_platforms)
  const visualStyle = asText(profile.visual_style)
  const tags = asStringArray(profile.tags)
  const speakingSample = asText(profile.speaking_style_sample)
  const voiceStyle = asText(profile.voice_style)
  const traitScoresRaw = profile.trait_scores
  const traitScores =
    traitScoresRaw && typeof traitScoresRaw === 'object' && !Array.isArray(traitScoresRaw)
      ? (traitScoresRaw as Record<string, unknown>)
      : null
  const numericTraitScores = useMemo(() => {
    if (!traitScores) return {}
    const out: Record<string, number> = {}
    for (const [k, v] of Object.entries(traitScores)) {
      if (typeof v === 'number') out[k] = v
    }
    return out
  }, [traitScores])
  const hasRadarData = Object.keys(numericTraitScores).length > 0
  const personaTraits = asStringArray(profile.persona_traits)

  const hasOverview = Boolean(coreValueProposition || contentDirection.length || commercialDirection.length || primaryPlatforms.length || visualStyle || growthPotential)

  return (
    <div className="tc-vip-step4-summary-col">
      {hasOverview && (
        <div className="tc-data-card tc-vip-step4-card" data-testid="vip-step4-overview">
          <div className="tc-vip-step4-card-title">
            <AimOutlined style={{ color: 'var(--vip-c-indigo)' }} />
            {t('step4.overviewTitle')}
          </div>
          <div className="tc-vip-step4-overview-rows">
            {coreValueProposition && <OverviewRow icon={<AimOutlined />} accent="indigo" heading={t('fields.corePositioning')} content={coreValueProposition} />}
            {contentDirection.length > 0 && (
              <OverviewRow icon={<CompassOutlined />} accent="coral" heading={t('fields.contentDirection')} content={<ChipList items={contentDirection} />} />
            )}
            {commercialDirection.length > 0 && (
              <OverviewRow icon={<DollarOutlined />} accent="green" heading={t('fields.businessModel')} content={<ChipList items={commercialDirection} />} />
            )}
            {primaryPlatforms.length > 0 && (
              <OverviewRow icon={<GlobalOutlined />} accent="orange" heading={t('fields.platformStrategy')} content={<PlatformChipList items={primaryPlatforms} />} />
            )}
            {visualStyle && <OverviewRow icon={<EyeOutlined />} accent="purple" heading={t('fields.visualDirection')} content={visualStyle} />}
            {growthPotential && (
              <OverviewRow
                icon={<RocketOutlined />}
                accent="blue"
                heading={t('fields.growthPotential')}
                content={GROWTH_POTENTIAL_KEYS[growthPotential] ? t(GROWTH_POTENTIAL_KEYS[growthPotential]) : growthPotential}
              />
            )}
          </div>
        </div>
      )}

      <div className="tc-data-card tc-vip-step4-card" data-testid="vip-step4-radar-card">
        <div className="tc-vip-step4-card-title">{t('step4.traitRadarTitle')}</div>
        {hasRadarData ? (
          <TraitRadar scores={numericTraitScores} />
        ) : personaTraits.length > 0 ? (
          <>
            <p className="tc-vip-card-hint">{t('step4.traitRadarFallback')}</p>
            <div className="tc-vip-step4-trait-chip-fallback">
              <ChipList items={personaTraits} />
            </div>
          </>
        ) : (
          <p className="tc-vip-card-hint">{t('step4.noTraitData')}</p>
        )}
      </div>

      {tags.length > 0 && (
        <div className="tc-data-card tc-vip-step4-card" data-testid="vip-step4-keywords-card">
          <div className="tc-vip-step4-card-title">{t('step4.coreKeywords')}</div>
          <div className="tc-vip-step4-chip-row">
            {tags.map((tag, idx) => (
              <Tag key={idx} className="tc-vip-analysis-tag tc-vip-step4-keyword-chip">
                {tag}
              </Tag>
            ))}
          </div>
        </div>
      )}

      {(speakingSample || voiceStyle) && (
        <div className="tc-data-card tc-vip-step4-card" data-testid="vip-step4-voice-card">
          <div className="tc-vip-step4-card-title">
            <SoundOutlined style={{ color: 'var(--vip-c-indigo)' }} />
            {t('step4.speakingStyleExamples')}
          </div>
          {speakingSample ? (
            <blockquote className="tc-vip-step4-speaking-sample">{speakingSample}</blockquote>
          ) : (
            <p className="tc-vip-card-hint">{t('step4.noSpeakingStyleSample')}</p>
          )}
          {voiceStyle && <div className="tc-vip-step4-voice-style-line">{t('step4.voiceStyleLine', { style: voiceStyle })}</div>}
          <div className="tc-vip-step4-audio-note">
            <SoundTwoTone twoToneColor="#7c8aa0" />
            {t('step4.voiceSampleLater')}
          </div>
        </div>
      )}
    </div>
  )
}

function OverviewRow({
  icon,
  accent,
  heading,
  content,
}: {
  icon: ReactNode
  accent: keyof typeof FIELD_ACCENTS
  heading: string
  content: ReactNode
}) {
  const a = FIELD_ACCENTS[accent]
  return (
    <div className="tc-vip-step4-overview-row">
      <span className="tc-vip-field-icon" style={{ '--field-color': a.color, '--field-bg': a.bg } as CSSProperties} aria-hidden>
        {icon}
      </span>
      <div>
        <div className="tc-vip-step4-overview-heading">{heading}</div>
        <div className="tc-vip-step4-overview-content">{content}</div>
      </div>
    </div>
  )
}

function TraitRadar({ scores }: { scores: Record<string, number> }) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const entries = Object.entries(scores)
  if (entries.length === 0) return <p className="tc-vip-card-hint">{t('common.noData')}</p>

  const size = 220
  const center = size / 2
  const maxRadius = center - 36
  const levels = 4

  const angleStep = (2 * Math.PI) / entries.length
  const startAngle = -Math.PI / 2

  const pointAt = (index: number, value: number) => {
    const angle = startAngle + index * angleStep
    const r = (Math.min(100, Math.max(0, value)) / 100) * maxRadius
    return { x: center + r * Math.cos(angle), y: center + r * Math.sin(angle) }
  }

  const dataPoints = entries.map(([, v], i) => pointAt(i, v))
  const polygon = dataPoints.map((p) => `${p.x},${p.y}`).join(' ')

  return (
    <div className="tc-vip-radar-wrap">
      <svg viewBox={`0 0 ${size} ${size}`} className="tc-vip-radar-svg" role="img" aria-label={t('step3.radarAriaLabel')}>
        {Array.from({ length: levels }, (_, li) => {
          const r = ((li + 1) / levels) * maxRadius
          const ring = entries
            .map((_, i) => {
              const angle = startAngle + i * angleStep
              return `${center + r * Math.cos(angle)},${center + r * Math.sin(angle)}`
            })
            .join(' ')
          return <polygon key={li} points={ring} className="tc-vip-radar-grid" />
        })}
        {entries.map((_, i) => {
          const angle = startAngle + i * angleStep
          return (
            <line
              key={i}
              x1={center}
              y1={center}
              x2={center + maxRadius * Math.cos(angle)}
              y2={center + maxRadius * Math.sin(angle)}
              className="tc-vip-radar-axis"
            />
          )
        })}
        <polygon points={polygon} className="tc-vip-radar-data" />
        {dataPoints.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={3} className="tc-vip-radar-dot" />
        ))}
      </svg>
      <ul className="tc-vip-radar-legend">
        {entries.map(([label, value]) => (
          <li key={label}>
            <span className="tc-vip-radar-legend-label">{formatTraitLabel(label)}</span>
            <span className="tc-vip-radar-legend-value">{value}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function formatTraitLabel(key: string): string {
  return key.replace(/_/g, ' ')
}

/** Right column — STEP2/STEP3 lineage + forward-looking hint + help
 * (spec §23–§26). The t('step4.nextCardPendingTitle') card intentionally does NOT render a
 * checklist of unlocked capabilities: an audit of the current product
 * found no live feature that is actually gated on Virtual IP
 * confirmation (Content Publishing Center, Video Director's "脚本与
 * 分镜" stage, etc. are all independent of this flow, and "脚本与分镜"
 * is itself still labeled t('common.comingSoon') elsewhere in the product) — so
 * this uses the same honest, single-line navigational hint from the
 * original STEP 4 spec ("确认后将进入下一阶段：IP 形象与内容打造")
 * instead of hardcoding the mockup's four-item promise. */
function ConfirmationSidebar({
  profile,
  typeRec,
  proposals,
  isConfirmed,
  onGoToStep5,
}: {
  profile: Record<string, unknown>
  typeRec: VirtualIpTypeRecommendation | null
  proposals: VirtualIpProposals | null
  isConfirmed: boolean
  onGoToStep5: () => void
}) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  // Identity + Run ID lineage come from the profile itself — the exact
  // STEP 2/3 sources `build_positioning_profile_v1` used to build THIS
  // profile. `typeRec` / `proposals` are consulted only for the
  // suitability/match scores, which the profile schema does not carry.
  const step2Display = asText(profile.selected_ip_type_display_name) || asText(profile.selected_ip_type)
  const step2Snapshot = typeRec?.selected?.snapshot
  const step2Score = step2Snapshot && typeof step2Snapshot.suitability_score === 'number' ? step2Snapshot.suitability_score : null
  const step2RunId = asText(profile.source_step2_run_id) || typeRec?.selected?.source_run_id || null

  const step3Name = asText(profile.name)
  const step3Label = asText(profile.positioning_label)
  const step3Score = proposalMatchScore(proposals)
  const step3RunId = asText(profile.source_step3_run_id) || proposals?.run_id || null

  return (
    <aside className="tc-vip-step4-sidebar">
      {(step2Display || step3Name) && (
        <div className="tc-data-card tc-vip-step4-card" data-testid="vip-step4-lineage-card">
          <div className="tc-vip-step4-card-title">{t('step4.currentConfirmInfo')}</div>

          {step2Display && (
            <div className="tc-vip-step4-lineage-block" data-testid="vip-step4-lineage-step2">
              <span className="tc-vip-step4-lineage-eyebrow">{t('step4.lineageIpType')}</span>
              <div className="tc-vip-step4-lineage-main">
                <CheckCircleFilled style={{ color: 'var(--success-text)' }} />
                <span>{step2Display}</span>
                {step2Score !== null && <span className="tc-vip-step4-lineage-score">{t('common.fitScore', { score: step2Score })}</span>}
              </div>
              {step2RunId && <RunIdLine runId={step2RunId} />}
            </div>
          )}

          {step3Name && (
            <div className="tc-vip-step4-lineage-block" data-testid="vip-step4-lineage-step3">
              <span className="tc-vip-step4-lineage-eyebrow">{t('step4.lineageProposal')}</span>
              <div className="tc-vip-step4-lineage-main">
                <CheckCircleFilled style={{ color: 'var(--success-text)' }} />
                <span>
                  {step3Name}
                  {step3Label ? `「${step3Label}」` : ''}
                </span>
                {step3Score !== null && <span className="tc-vip-step4-lineage-score">{t('common.matchScore', { score: step3Score })}</span>}
              </div>
              {step3RunId && <RunIdLine runId={step3RunId} />}
            </div>
          )}
        </div>
      )}

      <div className="tc-data-card tc-vip-step4-card" data-testid="vip-step4-next-stage-card">
        <div className="tc-vip-step4-card-title">{isConfirmed ? t('step4.nextCardConfirmedTitle') : t('step4.nextCardPendingTitle')}</div>
        <div className="tc-vip-step4-next-stage">
          <RocketOutlined className="tc-vip-step4-next-stage-icon" />
          <span>{isConfirmed ? t('step4.nextCardConfirmedDesc') : t('step4.nextCardPendingDesc')}</span>
        </div>
        {isConfirmed && (
          <Button type="primary" size="small" onClick={onGoToStep5} style={{ marginTop: 10 }} data-testid="vip-step4-sidebar-next-phase-button">
            {t('step4.buildCharacterArrow')}
          </Button>
        )}
      </div>

      <div className="tc-data-card tc-vip-step4-card" data-testid="vip-step4-help-card">
        <div className="tc-vip-step4-card-title">{t('step5.helpTitle')}</div>
        <div className="tc-vip-step4-help-block">
          <RobotOutlined className="tc-vip-step4-help-illustration" style={{ fontSize: 22 }} />
          <p className="tc-vip-step4-help-text">
            {t('step4.confirmHelpText')}
          </p>
        </div>
      </div>
    </aside>
  )
}

function RunIdLine({ runId }: { runId: string }) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  return (
    <div className="tc-vip-step4-lineage-runid">
      <span>Run ID: {runId.slice(0, 10)}…</span>
      <Tooltip title={t('common.copyRunId')}>
        <span className="tc-vip-step4-runid-copy" onClick={() => copyToClipboard(runId, t)}>
          <CopyOutlined />
        </span>
      </Tooltip>
    </div>
  )
}
